import React from 'react';

import { useConversationWithMessagesCachedState } from '../../../../../redux/hooks/answer/conversation-hooks';

import { MessageToolCallContext } from './context';

export interface IMessageToolCallProviderProps {
    conversationId: string;
    messageId: string;
    children: React.ReactNode;
}

/**
 * MessageToolCallProvider is a React functional component that provides context
 * for managing tool call and tool call result data associated with a specific message
 * within a conversation. It computes and prepares contextual data for its children components.
 *
 * This component utilizes state from the project and selected conversation to process data
 * related to tool calls and their results. The computed data is provided through the
 * `MessageToolCallContext.Provider` for other components to consume.
 *
 * Props:
 * - children: ReactNode - The child components that will receive the context value.
 * - messageId: string - The unique identifier of the message to retrieve tool call data for.
 * - conversationId: string - The unique identifier of the conversation containing the message.
 *
 * Context Value:
 * The following properties are provided through context:
 * - isLoading: boolean - Indicates whether the conversation data is still being loaded.
 * - toolCallsWithResults: Array<Object> - An array of processed tool call results,
 *   including their associated arguments.
 * - conversationId: string - The ID of the conversation being referenced.
 * - messageId: string - The ID of the message being analyzed.
 *
 * Internal Logic:
 * - Utilizes `useProjectFromState` to determine the current active project.
 * - Selects the appropriate conversation data by leveraging Redux selectors.
 * - Extracts and processes tool calls and results from the selected conversation data
 *   to provide enriched information in the context.
 */
export const MessageToolCallProvider: React.FC<IMessageToolCallProviderProps> = ({
    children,
    messageId,
    conversationId,
}) => {
    const { conversation, isLoading } = useConversationWithMessagesCachedState(conversationId);

    const value = React.useMemo(() => {
        const message = conversation?.messages.find((message) => message.message_id === messageId);
        const toolCalls = message?.tool_calls ?? [];
        const toolCallsResults = message?.tool_calls_results ?? [];

        const results = toolCallsResults.map((result) => {
            const toolCall = toolCalls.find((toolCall) => toolCall.tool_call_id === result.tool_call_id);

            let toolCallArguments = {};
            if (toolCall) {
                try {
                    toolCallArguments = JSON.parse(toolCall.function_args);
                } catch {}
            }

            return {
                ...result,
                tool_call_arguments: toolCallArguments,
            };
        });

        return { isLoading, toolCallsWithResults: results, conversationId, messageId };
    }, [conversation?.messages, isLoading, messageId, conversationId]);

    return <MessageToolCallContext.Provider value={value}>{children}</MessageToolCallContext.Provider>;
};
